import { LocalStorage } from '@novakid/frontend-core';
import React, { useEffect, useState } from 'react';

const DEFAULT_REGION = 'RU';

async function fetchRegion(): Promise<string> {
  return Promise.race([
    fetch('https://cdn.novakidschool.com/get-region')
      .then((res) => res.json())
      .then((res) => {
        const region = res['country_code'] || DEFAULT_REGION;
        LocalStorage.setNVKItem('NVK_GEO_REGION', region);
        return region;
      })
      .catch(() => DEFAULT_REGION),
    new Promise<string>((_, reject) => setTimeout(reject, 2000)),
  ]).catch(() => DEFAULT_REGION);
}

function injectGTM(gtmId: string) {
  if (!gtmId || document.getElementById('gtm-script')) return;

  const script = document.createElement('script');
  script.id = 'gtm-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // @ts-expect-error gtm event
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
}

function overrideRequestsIfRU(region: string) {
  if (region !== 'RU') return;

  const target = '/api/0/';
  const origin = window.location.origin;
  const ruOrigin = origin.replace('novakidschool.com', 'novakid.ru');
  const ruPath = `${ruOrigin}${target}`;

  const originalFetch = window.fetch;

  window.fetch = async (input, init) => {
    try {
      if (typeof input === 'string') {
        const isRelativeMatch = input.startsWith(target);
        const isAbsoluteMatch = input.startsWith(`${origin}${target}`);

        if (isRelativeMatch) {
          input = input.replace(target, ruPath);
        } else if (isAbsoluteMatch) {
          input = input.replace('novakidschool.com', 'novakid.ru');
        }

        return originalFetch(input, init);
      }

      if (input instanceof Request) {
        const originalUrl = input.url;
        const isRelativeMatch = originalUrl.startsWith(target);
        const isAbsoluteMatch = originalUrl.startsWith(`${origin}${target}`);

        let finalUrl = originalUrl;
        if (isRelativeMatch) {
          finalUrl = originalUrl.replace(target, ruPath);
        } else if (isAbsoluteMatch) {
          finalUrl = originalUrl.replace('novakidschool.com', 'novakid.ru');
        }

        const method = input.method.toUpperCase();
        const hasBody = method !== 'GET' && method !== 'HEAD';

        const clonedBody = hasBody ? await input.clone().text() : undefined;

        input = new Request(finalUrl, {
          method,
          headers: input.headers,
          body: clonedBody,
          mode: input.mode,
          credentials: input.credentials,
          cache: input.cache,
          redirect: input.redirect,
          referrer: input.referrer,
          integrity: input.integrity,
          keepalive: input.keepalive,
          signal: input.signal,
        });
      }
    } catch (err) {
      console.log(err, 'ERROR FROM FETCH HANDLER');
    }

    return originalFetch(input, init);
  };

  (function () {
    const OriginalXHR = window.XMLHttpRequest;

    function PatchedXHR(this: XMLHttpRequest) {
      const xhr = new OriginalXHR();

      const originalOpen = xhr.open;

      xhr.open = function (method: string, url: string, async: boolean = true, username?: string | null, password?: string | null) {
        const ruOrigin = window.location.origin.replace('novakidschool.com', 'novakid.ru');
        const target = '/api/0/';
        const ruPrefix = ruOrigin + target;

        let absoluteUrl = url;
        if (url.startsWith('/')) {
          absoluteUrl = window.location.origin + url;
        } else if (!url.startsWith('http')) {
          absoluteUrl = window.location.origin + '/' + url;
        }

        if (absoluteUrl.startsWith(window.location.origin + target)) {
          url = absoluteUrl.replace(window.location.origin + target, ruPrefix);
        }

        return originalOpen.call(this, method, url, async, username, password);
      };

      return xhr;
    }

    window.XMLHttpRequest = PatchedXHR as any;
  })();

  (function () {
    const originalSendBeacon = navigator.sendBeacon;

    navigator.sendBeacon = function (url: string | URL, data?: BodyInit | null): boolean {
      try {
        const urlStr = url.toString();

        const origin = window.location.origin;
        const ruOrigin = origin.replace('novakidschool.com', 'novakid.ru');
        const target = '/api/0/';
        const ruPrefix = ruOrigin + target;

        let absoluteUrl = urlStr;
        if (urlStr.startsWith('/')) {
          absoluteUrl = origin + urlStr;
        } else if (!urlStr.startsWith('http')) {
          absoluteUrl = origin + '/' + urlStr;
        }

        if (absoluteUrl.startsWith(origin + target)) {
          url = absoluteUrl.replace(origin + target, ruPrefix);
        }
      } catch (err) {
        console.warn('Error in sendBeacon override:', err);
      }

      return originalSendBeacon.call(this, url, data);
    };
  })();
}

export function withGeoHandling(App: React.ComponentType) {
  return function WithGeoHandlingComponent() {
    const [ready, setReady] = useState(false);

    useEffect(() => {
      fetchRegion().then((region) => {
        overrideRequestsIfRU(region);

        if (region !== 'RU') {
          injectGTM(process.env.REACT_APP_GTM_ID || '');
        }

        setReady(true);
      });
    }, []);

    if (!ready) return null;

    return <App />;
  };
}
